// ─────────────────────────────────────────────────────────────────────────────
// ListAliasesComponent
// ─────────────────────────────────────────────────────────────────────────────
// Paginated alias table with search, filter, sort, checkbox selection,
// product links, and SKU warning icons.
// Responsibilities:
//   - Dispatch LoadAliases and display the paginated result
//   - Local search with debounce, type/source filter dropdowns
//   - Client-side column sorting (backend does not support sort params)
//   - Checkbox selection for bulk delete
//   - Clickable product links opening product detail in a new tab
//   - Warning icon on rows with missing/empty SKU
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { SelectionController } from 'src/app/shared/utils/selection-controller';
import { UiModule } from 'src/app/ui/ui.module';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { CustomersService } from 'src/app/features/customers/services/customers.services';

import { AliasFilters, IProductAlias } from '../../models/alias.models';
import { AliasService } from '../../services/alias.service';
import { AddAliasComponent } from '../add-alias/add-alias.component';
import { ImportKnowledgeBaseDialogComponent } from '../import-knowledge-base-dialog/import-knowledge-base-dialog.component';
import { FilterDialogComponent } from 'src/app/shared/components/filter-dialog/filter-dialog.component';
import { FilterDialogData, FilterFieldOption } from 'src/app/shared/components/filter-dialog/filter-dialog.models';
import { selectAliasItems, selectAliasLoading, selectAliasMetadata } from '../../store/aliases.selectors';
import {
  BulkDeleteAliases,
  BulkDeleteAliasesSuccess,
  CreateAliasSuccess,
  DeleteAlias,
  DeleteAliasSuccess,
  LoadAliases,
  SetAliasFilters,
  SetAliasSearch,
  UpdateAliasSuccess,
} from '../../store/aliases.actions';

type SortableField = 'product_name' | 'sku' | 'alias_value' | 'alias_type' | 'customer_name' | 'source' | 'create_date';

@Component({
  selector: 'app-list-aliases',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './list-aliases.component.html',
  styleUrls: ['./list-aliases.component.scss'],
})
export class ListAliasesComponent implements OnInit, OnDestroy {
  // ── Store selectors ───────────────────────────────────────────────────────

  aliases$: Observable<IProductAlias[]> = this.store.select(selectAliasItems);
  metadata$: Observable<IPagination | null> = this.store.select(selectAliasMetadata);
  loading$: Observable<boolean> = this.store.select(selectAliasLoading);

  // ── Local state ───────────────────────────────────────────────────────────

  page: IPageCount = { number: 1, size: DEFAULT_PAGE_SIZE };
  keyword = '';
  filters: AliasFilters = { alias_type: '', source: '', customer_id: null, product_id: null, create_date_from: null, create_date_to: null };

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<IProductAlias>((row) => row.id);
  sortField: SortableField | '' = '';
  sortMode: 'asc' | 'desc' = 'asc';
  sortedAliases: IProductAlias[] = [];
  searchControl = new FormControl('');

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private aliasService: AliasService,
    private productsService: ProductsService,
    private customersService: CustomersService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Sync aliases from store to sortedAliases (server already returns sorted data)
    this.aliases$.pipe(takeUntil(this.destroy$)).subscribe((aliases) => {
      this.sortedAliases = [...aliases];
      this.selection.setRows(this.sortedAliases);
    });

    // Debounced search input
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.onSearch(value ?? '');
    });

    // Refresh list after single delete success
    this.actions$.pipe(ofType(DeleteAliasSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
      this.loadAliases();
    });

    // Refresh list after bulk delete / create / update success
    this.actions$.pipe(ofType(BulkDeleteAliasesSuccess, CreateAliasSuccess, UpdateAliasSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
      this.loadAliases();
    });

    // Initial load
    this.loadAliases();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  loadAliases(): void {
    this.store.dispatch(
      LoadAliases({
        page: this.page,
        key: this.keyword || undefined,
        filters: this.filters,
        sortBy: this.sortField || undefined,
        sortMode: this.sortField ? this.sortMode : undefined,
      }),
    );
  }

  toPage(page: IPageCount): void {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.loadAliases();
  }

  // ── Search ────────────────────────────────────────────────────────────────

  onSearch(value: string): void {
    this.keyword = value;
    this.store.dispatch(SetAliasSearch({ keyword: value }));
    this.page = { ...this.page, number: 1 };
    this.loadAliases();
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  activeFilterValues: Record<string, any> = {};

  private get aliasFilterConfig(): FilterDialogData {
    return {
      config: {
        title: 'knowledge_base.aliases.filter.title',
        sections: [
          {
            title: 'knowledge_base.aliases.filter.group_title',
            fields: [
              {
                key: 'product',
                label: 'filter.product',
                type: 'autocomplete',
                placeholder: 'filter.product_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.productsService.fetchProductsBySearch({ number: 1, size: 20 }, { filters: [] }, term, undefined, false, true).pipe(
                    map((res) =>
                      (res.data ?? [])
                        .filter((p) => p.id != null && p.sku)
                        .map((p) => ({
                          value: p.id!,
                          label: `${p.name} (${p.sku})`,
                        })),
                    ),
                  ),
              },
              {
                key: 'customer',
                label: 'filter.customer',
                type: 'autocomplete',
                placeholder: 'filter.customer_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.customersService
                    .listCustomers({ number: 1, size: 20 }, undefined, term)
                    .pipe(map((res) => (res.data ?? []).map((c) => ({ value: c.id, label: c.name })))),
              },
              {
                key: 'alias_type',
                label: 'knowledge_base.aliases.col.type',
                type: 'single-select',
                options: [
                  { value: 'name', label: 'knowledge_base.aliases.type.name' },
                  { value: 'code', label: 'knowledge_base.aliases.type.code' },
                  { value: 'description', label: 'knowledge_base.aliases.type.description' },
                ],
              },
              {
                key: 'source',
                label: 'knowledge_base.aliases.col.source',
                type: 'single-select',
                options: [
                  { value: 'import', label: 'knowledge_base.aliases.source.import' },
                  { value: 'manual', label: 'knowledge_base.aliases.source.manual' },
                  { value: 'rule', label: 'knowledge_base.aliases.source.rule' },
                ],
              },
              {
                key: 'create_date',
                label: 'filter.created_date',
                type: 'date-range',
                minKey: 'create_date_from',
                maxKey: 'create_date_to',
                minPlaceholder: 'common.from',
                maxPlaceholder: 'common.to',
              },
            ],
          },
        ],
      },
      values: {},
    };
  }

  setFilter(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      data: {
        ...this.aliasFilterConfig,
        values: { ...this.activeFilterValues },
      } as FilterDialogData,
      width: '900px',
    });

    dialogRef.afterClosed().subscribe((result: Record<string, any> | undefined) => {
      if (result) {
        this.activeFilterValues = result;
        this.filters = {
          alias_type: (result['alias_type'] || '') as AliasFilters['alias_type'],
          source: (result['source'] || '') as AliasFilters['source'],
          customer_id: result['customer']?.value ?? null,
          product_id: result['product']?.value ?? null,
          create_date_from: result['create_date_from'] || null,
          create_date_to: result['create_date_to'] || null,
        };
        this.store.dispatch(SetAliasFilters({ filters: this.filters }));
        this.page = { ...this.page, number: 1 };
        this.loadAliases();
      }
    });
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filters.alias_type ||
      this.filters.source ||
      this.filters.customer_id ||
      this.filters.product_id ||
      this.filters.create_date_from ||
      this.filters.create_date_to
    );
  }

  clearFilter(): void {
    this.activeFilterValues = {};
    this.filters = { alias_type: '', source: '', customer_id: null, product_id: null, create_date_from: null, create_date_to: null };
    this.store.dispatch(SetAliasFilters({ filters: this.filters }));
    this.page = { ...this.page, number: 1 };
    this.loadAliases();
  }

  // ── Sorting (server-side) ─────────────────────────────────────────────────

  onSort(field: SortableField): void {
    if (this.sortField === field) {
      this.sortMode = this.sortMode === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortMode = 'asc';
    }
    this.page = { ...this.page, number: 1 };
    this.loadAliases();
  }

  // ── Checkbox selection ────────────────────────────────────────────────────


  // ── Dialogs ───────────────────────────────────────────────────────────────

  openCreateDialog(): void {
    this.dialog.open(AddAliasComponent, {
      data: null,
      disableClose: true,
    });
  }

  openEditDialog(alias: IProductAlias): void {
    this.dialog.open(AddAliasComponent, {
      data: alias,
      disableClose: true,
    });
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open(ImportKnowledgeBaseDialogComponent, {
      data: { type: 'alias' },
      disableClose: true,
      width: '700px',
      maxWidth: '95vw',
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadAliases();
      }
    });
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  deleteAlias(alias: IProductAlias): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.store.dispatch(DeleteAlias({ id: alias.id }));
      }
    });
  }

  bulkDelete(): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.store.dispatch(BulkDeleteAliases({ ids: this.selection.ids }));
      }
    });
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  exportList(): void {
    this.aliasService
      .exportAliases('xlsx', this.keyword || undefined, this.filters, this.sortField || undefined, this.sortField ? this.sortMode : undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `product_aliases_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getProductLink(alias: IProductAlias): string {
    return `/inventory/${alias.product_id}`;
  }

  hasInvalidSku(alias: IProductAlias): boolean {
    return !alias.product_id;
  }

}
